/**
 * The arithmetic behind tying a Resource Plan to one NOS Demand
 * (workspace feat-link-resource-plans-to-nos1-demands). Pure: no database, no Gateway —
 * src/api/plan-nos-demand.ts loads the inputs and writes the answers.
 *
 * THE GRAIN PROBLEM. NOS keeps guidance per person per month (DemandPerson × Period). A
 * plan keeps hours per Plan Line per month, and a line names at most one Resource — or,
 * once split, several Shares, one Resource each. So both directions go through "slots":
 * one per unsplit line (its Suggested Resource, or nobody) and one per Share. A Resource
 * reaches NOS through its `external_nymbl_person` link.
 *
 * LINKING (closed months). NOS has settled a closed month, so its numbers win. For every
 * closed plan month:
 *   - a person NOS has hours for gets those hours on the slots that name them — split
 *     across several slots in proportion to what the plan already had there, or all on
 *     the first when the plan had nothing;
 *   - every other slot, including a line with nobody named, ends at nothing;
 *   - the month's ramp goes back to 1.0, so entered hours ARE the hours NOS has.
 * People NOS has hours for who are on no slot are reported, never added (product decision
 * on the workspace card).
 *
 * POSTING (open months). The plan's effective hours per person per open month replace
 * NOS's guidance, including zero for a person on the Demand the plan no longer carries.
 * Every slot with open-month hours must name a Resource that maps to an NOS person first.
 *
 * Hours are integer centi-hours throughout; NOS's floats are rounded once on the way in.
 */

import { resourcingStatusLabel } from "@/lib/plan-status-labels";
import { formatHoursCenti, rampHoursCenti } from "@/lib/resource-plan-math";

const FULL_RAMP_MILLI = 1000;

/**
 * Why this plan can't be linked to a NOS Demand yet; null once Resourcing has marked it
 * Staffed (workspace feat-staffing-plan-status-workflow, 2026-10-28 — supersedes the
 * 2026-10-26 "any plan status"). Applies to a new link and to changing to another Demand;
 * a plan already linked keeps its link, and refreshing that Demand is not a new link.
 */
export function nosDemandLinkBlock(resourcingStatus: string | null | undefined): string | null {
  if (resourcingStatus === "Staffed") return null;
  return `A plan can be linked to a NOS Demand once Resourcing has marked it Staffed. This plan is ${resourcingStatusLabel(resourcingStatus)}.`;
}

/** One holder of a line's hours: the line itself when unsplit, or one of its Shares. */
export interface DemandSlot {
  lineId: string;
  /** Null when this slot IS the line (unsplit). */
  shareId: string | null;
  /** The Resource on the slot, or null for a line with nobody named. */
  resourceId: string | null;
  /** How the preview names the line ("AI Engineer · Build"). */
  lineLabel: string;
  /** Pinned month (`YYYY-MM-01`) → ENTERED (pre-ramp) centi-hours. */
  entered: ReadonlyMap<string, number>;
}

/** A person NOS has on the Demand. `personKey` is the lower-cased NOS PersonID. */
export interface DemandPerson {
  demandPersonId: string;
  personKey: string | null;
  name: string;
}

/** NOS hours as a float (null = no NOS row) → centi-hours (null stays null). */
export function nosHoursToCenti(hours: number | null): number | null {
  return hours === null ? null : Math.round(hours * 100);
}

/** The plan months on or before the closed-through month — the locked ones. */
export function lockedMonths(
  planMonths: readonly string[],
  closedThrough: string | null,
): string[] {
  if (closedThrough === null) return [];
  return planMonths.filter((m) => m <= closedThrough);
}

/**
 * The closed-through month NOS gives a plan: the latest plan month NOS has closed. NOS
 * closes Periods in order, so everything before it is closed too. Null when none is.
 */
export function closedThroughFor(
  planMonths: readonly string[],
  closedByMonth: ReadonlyMap<string, boolean>,
): string | null {
  let latest: string | null = null;
  for (const m of planMonths) if (closedByMonth.get(m) === true) latest = m;
  return latest;
}

function effective(slot: DemandSlot, month: string, ramp: ReadonlyMap<string, number>): number {
  return rampHoursCenti(slot.entered.get(month) ?? 0, ramp.get(month) ?? FULL_RAMP_MILLI);
}

// ── Linking ──────────────────────────────────────────────────────────────────

export interface ClosedMonthChange {
  lineId: string;
  lineLabel: string;
  /** Pinned month. */
  month: string;
  /** The line's effective hours before, and its hours after (ramp 1.0). */
  fromCenti: number;
  toCenti: number;
}

export interface UnplannedNosPerson {
  name: string;
  /** Total NOS hours across the plan's closed months. */
  hoursCenti: number;
  /** Pinned months with hours, oldest first. */
  months: string[];
}

export interface LinkPlan {
  /** Per slot, per closed month: the entered centi-hours to store (0 = no cell). */
  slotValues: Array<{ lineId: string; shareId: string | null; month: string; centi: number }>;
  /** Per line, per closed month: the line cell (for a split line, the sum of its Shares). */
  lineValues: Array<{ lineId: string; month: string; centi: number }>;
  /** Lines whose closed-month hours move, one row per line × month. */
  changes: ClosedMonthChange[];
  /** NOS people with closed-month hours who are on no slot. Listed, not added. */
  notOnPlan: UnplannedNosPerson[];
  /** Closed months whose ramp was below 1.0 — linking resets them to full. */
  rampResetMonths: string[];
}

/**
 * What linking does to the plan's closed months. `nosCenti` is NOS's guidance per
 * person key per closed month; `personKeyByResource` maps a Resource to its NOS person.
 */
export function planClosedMonthsFromNos(input: {
  slots: readonly DemandSlot[];
  closedMonths: readonly string[];
  rampMilliByMonth: ReadonlyMap<string, number>;
  nosCenti: ReadonlyMap<string, ReadonlyMap<string, number>>;
  nosNames: ReadonlyMap<string, string>;
  personKeyByResource: ReadonlyMap<string, string>;
}): LinkPlan {
  const { slots, closedMonths, rampMilliByMonth: ramp } = input;
  const keyOf = (s: DemandSlot) =>
    s.resourceId === null ? null : (input.personKeyByResource.get(s.resourceId) ?? null);

  const slotValues: LinkPlan["slotValues"] = [];
  const lineTotals = new Map<string, Map<string, number>>();
  const lineBefore = new Map<string, Map<string, number>>();
  const labels = new Map<string, string>();
  const covered = new Set<string>();

  for (const month of closedMonths) {
    // Which slots each person sits on, in plan order.
    const slotsByPerson = new Map<string, DemandSlot[]>();
    for (const s of slots) {
      const k = keyOf(s);
      if (k === null) continue;
      const list = slotsByPerson.get(k) ?? [];
      list.push(s);
      slotsByPerson.set(k, list);
    }

    const value = new Map<DemandSlot, number>();
    for (const [personKey, held] of slotsByPerson) {
      const hours = input.nosCenti.get(personKey)?.get(month) ?? 0;
      if (hours > 0) covered.add(personKey);
      const weights = held.map((s) => effective(s, month, ramp));
      const total = weights.reduce((a, b) => a + b, 0);
      if (total === 0) {
        held.forEach((s, i) => {
          value.set(s, i === 0 ? hours : 0);
        });
        continue;
      }
      // Floor each proportional part, then give what is left to the slot that had the
      // most — the parts always add back to exactly NOS's figure.
      let given = 0;
      let biggest = 0;
      held.forEach((s, i) => {
        const part = Math.floor((hours * (weights[i] ?? 0)) / total);
        value.set(s, part);
        given += part;
        if ((weights[i] ?? 0) > (weights[biggest] ?? 0)) biggest = i;
      });
      const top = held[biggest];
      if (top !== undefined) value.set(top, (value.get(top) ?? 0) + hours - given);
    }

    for (const s of slots) {
      const centi = value.get(s) ?? 0;
      slotValues.push({ lineId: s.lineId, shareId: s.shareId, month, centi });
      labels.set(s.lineId, s.lineLabel);
      const totals = lineTotals.get(s.lineId) ?? new Map<string, number>();
      totals.set(month, (totals.get(month) ?? 0) + centi);
      lineTotals.set(s.lineId, totals);
      const before = lineBefore.get(s.lineId) ?? new Map<string, number>();
      before.set(month, (before.get(month) ?? 0) + effective(s, month, ramp));
      lineBefore.set(s.lineId, before);
    }
  }

  const lineValues: LinkPlan["lineValues"] = [];
  const changes: ClosedMonthChange[] = [];
  for (const [lineId, totals] of lineTotals) {
    for (const [month, centi] of totals) {
      lineValues.push({ lineId, month, centi });
      const fromCenti = lineBefore.get(lineId)?.get(month) ?? 0;
      if (fromCenti !== centi) {
        changes.push({
          lineId,
          lineLabel: labels.get(lineId) ?? "",
          month,
          fromCenti,
          toCenti: centi,
        });
      }
    }
  }

  const onPlan = new Set(
    slots.flatMap((s) => {
      const k = keyOf(s);
      return k === null ? [] : [k];
    }),
  );
  const notOnPlan: UnplannedNosPerson[] = [];
  for (const [personKey, byMonth] of input.nosCenti) {
    if (onPlan.has(personKey) || covered.has(personKey)) continue;
    const months = closedMonths.filter((m) => (byMonth.get(m) ?? 0) > 0);
    if (months.length === 0) continue;
    notOnPlan.push({
      name: input.nosNames.get(personKey) ?? "Unnamed NOS person",
      hoursCenti: months.reduce((sum, m) => sum + (byMonth.get(m) ?? 0), 0),
      months,
    });
  }
  notOnPlan.sort((a, b) => a.name.localeCompare(b.name));

  return {
    slotValues,
    lineValues,
    changes,
    notOnPlan,
    rampResetMonths: closedMonths.filter(
      (m) => (ramp.get(m) ?? FULL_RAMP_MILLI) !== FULL_RAMP_MILLI,
    ),
  };
}

// ── The closed-month lock ────────────────────────────────────────────────────

/**
 * Every line's EFFECTIVE hours in the locked months, as `lineId|month` → centi-hours
 * (zeros left out, so "no cell" and "0" agree). An edit that changes this changed a
 * month NOS has closed, and is refused.
 */
export function lockedMonthFingerprint(input: {
  lines: ReadonlyArray<{ id: string; entered: ReadonlyMap<string, number> }>;
  lockedMonths: readonly string[];
  rampMilliByMonth: ReadonlyMap<string, number>;
}): Map<string, number> {
  const out = new Map<string, number>();
  for (const line of input.lines) {
    for (const month of input.lockedMonths) {
      const centi = rampHoursCenti(
        line.entered.get(month) ?? 0,
        input.rampMilliByMonth.get(month) ?? FULL_RAMP_MILLI,
      );
      if (centi !== 0) out.set(`${line.id}|${month}`, centi);
    }
  }
  return out;
}

/** The locked months whose hours differ between two fingerprints, oldest first. */
export function changedLockedMonths(
  before: ReadonlyMap<string, number>,
  after: ReadonlyMap<string, number>,
): string[] {
  const months = new Set<string>();
  for (const key of new Set([...before.keys(), ...after.keys()])) {
    if ((before.get(key) ?? 0) !== (after.get(key) ?? 0)) {
      months.add(key.slice(key.indexOf("|") + 1));
    }
  }
  return [...months].sort();
}

// ── Posting ──────────────────────────────────────────────────────────────────

export interface PostRow {
  personKey: string;
  personName: string;
  /** The DemandPerson NOS already has, or null when posting adds the person. */
  demandPersonId: string | null;
  /** Pinned month. */
  month: string;
  /** NOS's guidance before (null = no NOS row) and the plan's hours to write. */
  nosCenti: number | null;
  planCenti: number;
}

export interface PostPlan {
  /** Every person × open month whose NOS guidance changes. */
  rows: PostRow[];
  /** People the plan carries who are not on the Demand yet; posting adds them. */
  addsPeople: Array<{ personKey: string; personName: string; resourceId: string }>;
  /** Lines with open-month hours and nobody named — posting waits for these. */
  unnamedLines: Array<{ lineId: string; lineLabel: string; hoursCenti: number }>;
  /** Named Resources with open-month hours who have no NOS person link. */
  unmappedResources: Array<{ resourceId: string; name: string; hoursCenti: number }>;
}

/**
 * The person × month guidance a post would write. `openMonths` are the plan months NOS
 * has a Period for and has not closed. NOS rows outside them are never touched.
 */
export function planGuidancePost(input: {
  slots: readonly DemandSlot[];
  openMonths: readonly string[];
  rampMilliByMonth: ReadonlyMap<string, number>;
  personKeyByResource: ReadonlyMap<string, string>;
  resourceNames: ReadonlyMap<string, string>;
  demandPeople: readonly DemandPerson[];
  /** NOS guidance per person key per open month (null = no NOS row). */
  nosCenti: ReadonlyMap<string, ReadonlyMap<string, number | null>>;
}): PostPlan {
  const { openMonths, rampMilliByMonth: ramp } = input;

  const plan = new Map<string, Map<string, number>>();
  const resourceByKey = new Map<string, string>();
  const unnamed = new Map<string, { lineLabel: string; hoursCenti: number }>();
  const unmapped = new Map<string, number>();

  for (const s of input.slots) {
    const hours = openMonths.reduce((sum, m) => sum + effective(s, m, ramp), 0);
    if (s.resourceId === null) {
      if (hours > 0) {
        const prior = unnamed.get(s.lineId);
        unnamed.set(s.lineId, {
          lineLabel: s.lineLabel,
          hoursCenti: (prior?.hoursCenti ?? 0) + hours,
        });
      }
      continue;
    }
    const personKey = input.personKeyByResource.get(s.resourceId);
    if (personKey === undefined) {
      if (hours > 0) unmapped.set(s.resourceId, (unmapped.get(s.resourceId) ?? 0) + hours);
      continue;
    }
    resourceByKey.set(personKey, resourceByKey.get(personKey) ?? s.resourceId);
    const byMonth = plan.get(personKey) ?? new Map<string, number>();
    for (const m of openMonths) byMonth.set(m, (byMonth.get(m) ?? 0) + effective(s, m, ramp));
    plan.set(personKey, byMonth);
  }

  const onDemand = new Map<string, DemandPerson>();
  for (const p of input.demandPeople) {
    if (p.personKey !== null && !onDemand.has(p.personKey)) onDemand.set(p.personKey, p);
  }

  const nameOf = (personKey: string): string => {
    const resourceId = resourceByKey.get(personKey);
    return (
      (resourceId === undefined ? undefined : input.resourceNames.get(resourceId)) ??
      onDemand.get(personKey)?.name ??
      "Unnamed person"
    );
  };

  const rows: PostRow[] = [];
  const people = new Set([...plan.keys(), ...onDemand.keys()]);
  for (const personKey of people) {
    for (const month of openMonths) {
      const nosCenti = input.nosCenti.get(personKey)?.get(month) ?? null;
      const planCenti = plan.get(personKey)?.get(month) ?? 0;
      if ((nosCenti ?? 0) === planCenti) continue;
      rows.push({
        personKey,
        personName: nameOf(personKey),
        demandPersonId: onDemand.get(personKey)?.demandPersonId ?? null,
        month,
        nosCenti,
        planCenti,
      });
    }
  }
  rows.sort((a, b) => a.personName.localeCompare(b.personName) || a.month.localeCompare(b.month));

  const addsPeople = [...plan]
    .filter(([k, byMonth]) => !onDemand.has(k) && [...byMonth.values()].some((v) => v > 0))
    .map(([personKey]) => ({
      personKey,
      personName: nameOf(personKey),
      resourceId: resourceByKey.get(personKey) ?? "",
    }))
    .sort((a, b) => a.personName.localeCompare(b.personName));

  return {
    rows,
    addsPeople,
    unnamedLines: [...unnamed].map(([lineId, v]) => ({ lineId, ...v })),
    unmappedResources: [...unmapped].map(([resourceId, hoursCenti]) => ({
      resourceId,
      name: input.resourceNames.get(resourceId) ?? "Unnamed Resource",
      hoursCenti,
    })),
  };
}

/**
 * A short, stable digest of what a preview showed. The apply recomputes it and refuses
 * when it differs, so what is written is exactly what the planner reviewed.
 */
export function previewToken(parts: readonly unknown[]): string {
  return new Bun.CryptoHasher("sha256").update(JSON.stringify(parts)).digest("hex").slice(0, 24);
}

/** Centi-hours → NOS's float hours, exactly (two decimals). */
export function centiToNosHours(centi: number): number {
  return Number(formatHoursCenti(centi));
}

// ── Edits to post ────────────────────────────────────────────────────────────
//
// "Does the plan have hours NOS hasn't been given?" is answered without asking NOS: a
// digest of the open-month guidance is stored whenever plan and NOS are known to agree
// (linking takes NOS's side, a post takes the plan's), and the plan's own digest is
// compared against it on load. Both sides digest the same `p:person|month|centi` entries,
// zeros left out, so a plan that matches NOS hashes exactly as NOS does. Edits made in
// NOS directly are not seen here; the post preview still shows them.
//
// Both sides must cover the months a post can actually send. A post skips months NOS has
// no Period for yet, so when NOS lacked some of the plan's months the digest stops at the
// last month NOS had, and that bound is stored with it ("2027-06-01|<hash>") for the load
// to reuse. Months NOS adds later count once a refresh, post or preview re-reads NOS.

/** No month at all: the bound when NOS had none of the plan's open months. */
const NO_MONTH = "0000-01-01";

/**
 * The plan months a post can touch: after the closed-through month and, when NOS lacked
 * some, no later than the last month it had (`nosThrough`).
 */
export function guidanceMonths(
  planMonths: readonly string[],
  closedThrough: string | null,
  nosThrough: string | null = null,
): string[] {
  return planMonths.filter(
    (m) =>
      (closedThrough === null || m > closedThrough) && (nosThrough === null || m <= nosThrough),
  );
}

/**
 * The last of `candidates` NOS has a Period for, when it lacks some of them — null when it
 * has every one, or reported no months at all (a Demand with nobody on it, where a post
 * offers every month and the Gateway skips what NOS lacks).
 */
export function nosGuidanceThrough(
  candidates: readonly string[],
  nosMonths: ReadonlyMap<string, boolean>,
  hasMonths: boolean,
): string | null {
  if (!hasMonths || candidates.every((m) => nosMonths.has(m))) return null;
  let last = NO_MONTH;
  for (const m of candidates) if (nosMonths.has(m)) last = m;
  return last;
}

/** The stored form: the digest, after the NOS bound when there is one. */
export function syncedFingerprint(digest: string, nosThrough: string | null): string {
  return nosThrough === null ? digest : `${nosThrough}|${digest}`;
}

/** The NOS bound a stored digest was taken under (null = none). */
export function syncedNosThrough(stored: string): string | null {
  const bar = stored.indexOf("|");
  return bar === -1 ? null : stored.slice(0, bar);
}

function digestEntries(entries: readonly string[]): string {
  return previewToken([...entries].sort());
}

/**
 * The plan's side: each NOS person's effective hours per month. Hours on a line with
 * nobody named, or on a Resource with no NOS person, count too — they are edits NOS
 * hasn't got, even though posting waits until they have somewhere to go.
 */
export function planGuidanceFingerprint(input: {
  slots: readonly DemandSlot[];
  months: readonly string[];
  rampMilliByMonth: ReadonlyMap<string, number>;
  personKeyByResource: ReadonlyMap<string, string>;
}): string {
  const totals = new Map<string, number>();
  for (const s of input.slots) {
    const personKey =
      s.resourceId === null ? undefined : input.personKeyByResource.get(s.resourceId);
    const holder =
      s.resourceId === null
        ? `l:${s.lineId}`
        : personKey === undefined
          ? `r:${s.resourceId}`
          : `p:${personKey}`;
    for (const month of input.months) {
      const centi = effective(s, month, input.rampMilliByMonth);
      if (centi === 0) continue;
      const k = `${holder}|${month}`;
      totals.set(k, (totals.get(k) ?? 0) + centi);
    }
  }
  return digestEntries([...totals].filter(([, c]) => c !== 0).map(([k, c]) => `${k}|${c}`));
}

/** NOS's side: its guidance per person key per month, as `readDemand` sums it. */
export function nosGuidanceFingerprint(input: {
  guidanceByPerson: ReadonlyMap<string, ReadonlyMap<string, number | null>>;
  months: readonly string[];
}): string {
  const entries: string[] = [];
  for (const [personKey, byMonth] of input.guidanceByPerson) {
    for (const month of input.months) {
      const centi = byMonth.get(month) ?? null;
      if (centi !== null && centi !== 0) entries.push(`p:${personKey}|${month}|${centi}`);
    }
  }
  return digestEntries(entries);
}

/**
 * The address of the Demand Planning app (`DEMAND_PLANNING_URL`, set per environment). Unset
 * or not an http(s) origin is null: there is then no link, never a guessed host.
 */
export function demandPlanningBaseUrl(
  env: Record<string, string | undefined> = process.env,
): string | null {
  const raw = env.DEMAND_PLANNING_URL?.trim();
  if (!raw) return null;
  try {
    const url = new URL(raw);
    return url.protocol === "https:" || url.protocol === "http:" ? url.origin : null;
  } catch {
    return null;
  }
}

/**
 * Demand Planning's Create demand page for a NOS Account, opened on the plan's first month.
 * Staff knows no contract, so none is passed.
 */
export function demandPlanningCreateDemandUrl(
  baseUrl: string | null,
  nosAccountId: string | null,
  startDate: string | null,
): string | null {
  if (!baseUrl || !nosAccountId) return null;
  const params = new URLSearchParams({ accountId: nosAccountId });
  if (startDate) params.set("startDate", startDate);
  return `${baseUrl.replace(/\/+$/, "")}/dashboard/demands/new?${params}`;
}

/**
 * Who NOS refused to add, or null. An inactive row on the Demand is named as the cause;
 * NOS can't say why for anyone else, so that line gives the likely reasons.
 */
export function notAddedMessage(
  names: readonly string[],
  inactiveOnDemand: readonly string[],
): string | null {
  const others = names.filter((n) => !inactiveOnDemand.includes(n));
  const lines = [
    ...(inactiveOnDemand.length > 0
      ? [
          `${inactiveOnDemand.join(", ")}: already on this Demand as inactive in NOS, so their hours weren't posted. Make that row active, then post again.`,
        ]
      : []),
    ...(others.length > 0
      ? [
          `NOS didn't add ${others.join(", ")} to the Demand. Their hours weren't posted. Check they're active in NOS and the Demand is still open.`,
        ]
      : []),
  ];
  return lines.length > 0 ? lines.join(" ") : null;
}
